// « UTILISATEURS ET PERSONAS » : deux onglets sous une même entrée du menu.
//
//   · Annuaire — les VRAIES personnes du produit (migration 052), vivier des
//     études et des entretiens : on y range, on y filtre, on y importe.
//   · Personas — les PROFILS TYPES, inchangés. Une personne peut incarner un
//     persona ; le persona reste le profil.
import { useState, type ReactNode } from 'react';
import { usePersonas, useStudies } from '../api/queries';
import { Button, Chip, EmptyState, ErrorBanner, Eyebrow, Select, Spinner } from '../components/ui';
import { TabBar, useTabs } from '../components/Tabs';
import { seniorityLabel } from '../personas/PersonaNeeds';
import { PersonasScreen } from '../project/ListScreens';
import { useRouter } from '../router';
import { studyKind } from '../studies/kinds';
import { WriteAction } from '../write/Forms';
import { engagementLabel, OVER_SOLICITED, personName, presetPeople, usePeople, type Person } from './api';
import { FunBadges, hueOf, PersonAvatar, SeniorityStars, SolicitationChip } from './PersonBadges';
import { ImportPeople } from './ImportPeople';
import { filterPeople, NO_FILTER, PeopleFilters, type PeopleFilter } from './PeopleFilters';
import { PersonForm } from './PersonForm';
import './people.css';

type Tab = 'annuaire' | 'personas';

const TABS = ['annuaire', 'personas'] as const;

export function UsersAndPersonasScreen({ projectId }: { projectId: string }) {
  const [tab, setTab] = useTabs<Tab>('utilisateurs', TABS, 'annuaire');
  const people = usePeople(projectId);
  const personas = usePersonas(projectId);
  const tabs = (
    <TabBar
      label="Utilisateurs et personas"
      value={tab}
      onChange={setTab}
      tabs={[
        { id: 'annuaire', label: 'Annuaire', count: people.data?.people.length ?? null },
        { id: 'personas', label: 'Personas', count: personas.data?.length ?? null },
      ]}
    />
  );
  return tab === 'personas'
    ? <PersonasScreen projectId={projectId} tabs={tabs} />
    : <PeopleScreen projectId={projectId} tabs={tabs} />;
}

type GroupBy = 'pool' | 'persona' | 'role' | 'seniority' | 'none';

const GROUP_LABELS: Record<GroupBy, string> = {
  pool: 'Groupe (pool)', persona: 'Persona', role: 'Rôle', seniority: 'Séniorité', none: 'Aucun regroupement',
};

/** Les personnes rangées par groupe, dans l'ordre alphabétique des groupes. PUR. */
function groupPeople(people: Person[], by: GroupBy): { key: string; label: string; people: Person[] }[] {
  if (by === 'none') return [{ key: 'tous', label: '', people }];
  const label = (p: Person) => (by === 'pool' ? p.pool
    : by === 'persona' ? p.persona_name
      : by === 'role' ? p.role
        : seniorityLabel(p.seniority)) || 'Non renseigné';
  const map = new Map<string, Person[]>();
  for (const p of people) {
    const k = label(p);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(p);
  }
  return [...map.entries()]
    .sort(([a], [b]) => (a === 'Non renseigné' ? 1 : b === 'Non renseigné' ? -1 : a.localeCompare(b, 'fr')))
    .map(([k, list]) => ({ key: k, label: k, people: list }));
}

function PeopleScreen({ projectId, tabs }: { projectId: string; tabs: ReactNode }) {
  const query = usePeople(projectId);
  const { navigate } = useRouter();
  const [filter, setFilter] = useState<PeopleFilter>(NO_FILTER);
  const [groupBy, setGroupBy] = useState<GroupBy>('pool');
  const [selected, setSelected] = useState<string[]>([]);
  const data = query.data;
  const canWrite = data?.canWrite ?? false;
  const pools = data?.pools ?? [];
  const roles = data?.roles ?? [];
  const all = data?.people ?? [];
  const shown = filterPeople(all, filter);
  const groups = groupPeople(shown, groupBy);
  const chosen = new Set(selected);
  const pickable = (p: Person) => !p.do_not_contact;
  const toggle = (id: string) => setSelected(chosen.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const toggleGroup = (list: Person[]) => {
    const ids = list.filter(pickable).map((p) => p.id);
    const all = ids.every((id) => chosen.has(id));
    setSelected(all ? selected.filter((x) => !ids.includes(x)) : [...new Set([...selected, ...ids])]);
  };
  const open = (p: Person) => navigate(`/projet/${encodeURIComponent(projectId)}/personnes/${encodeURIComponent(p.id)}`);

  const actions = canWrite ? (
    <>
      <WriteAction label="Importer une liste" title="Importer une liste de personnes" accent wide>
        {(done) => <ImportPeople projectId={projectId} onDone={done} />}
      </WriteAction>
      <WriteAction label="Ajouter une personne" title="Ajouter une personne à l’annuaire">
        {(done) => <PersonForm projectId={projectId} pools={pools} roles={roles} onDone={done} />}
      </WriteAction>
    </>
  ) : null;

  return (
    <main className="screen">
      {tabs}
      <div className="zy-page-head__texte">
        <Eyebrow>Utilisateurs et personas</Eyebrow>
        <h1 className="t-h1">Annuaire</h1>
        <p className="zy-intro">
          Les vraies personnes qui utilisent le produit : le vivier où l’on pioche les participants des études
          et des entretiens. Cochez une personne ou tout un groupe pour les inviter.
        </p>
        {actions ? <div className="screen-actions">{actions}</div> : null}
      </div>

      {query.isPending ? (
        <div className="screen-loading"><Spinner label="Chargement de l’annuaire" /></div>
      ) : query.isError ? (
        <>
          <ErrorBanner>{query.error instanceof Error ? query.error.message : 'Chargement impossible.'}</ErrorBanner>
          <Button variant="ghost" onClick={() => void query.refetch()}>Réessayer</Button>
        </>
      ) : all.length === 0 ? (
        <EmptyState
          title="Personne dans l’annuaire"
          description="Importez la liste des utilisateurs du produit (copier-coller depuis Excel ou fichier CSV) : vous pourrez ensuite les filtrer par pool, rôle, séniorité ou engagement, et les inviter aux entretiens en quelques clics."
        />
      ) : (
        <>
          <PeopleFilters value={filter} onChange={setFilter} pools={pools} roles={roles} people={all} />
          <div className="ppl-toolbar">
            <span className="zy-texte-muet">
              {shown.length === all.length ? `${all.length} personne${all.length > 1 ? 's' : ''}` : `${shown.length} sur ${all.length} personnes`}
              {` · 🌟 ${all.filter((p) => p.key_user).length} key users · 💪 ${all.filter((p) => p.domain_expert).length} experts métier`}
            </span>
            <label className="ppl-groupby">
              <span>Grouper par</span>
              <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
                {(Object.keys(GROUP_LABELS) as GroupBy[]).map((g) => <option key={g} value={g}>{GROUP_LABELS[g]}</option>)}
              </Select>
            </label>
          </div>
          {shown.length === 0 ? (
            <p className="zy-texte-muet">Rien ne correspond aux filtres.</p>
          ) : (
            groups.map((g) => {
              const ids = g.people.filter(pickable).map((p) => p.id);
              const allIn = ids.length > 0 && ids.every((id) => chosen.has(id));
              return (
                <section key={g.key} className="ppl-group">
                  {g.label ? (
                    <div className="ppl-group-head">
                      {groupBy === 'pool' ? <span className={`ppl-group-dot hue-${hueOf(g.label === 'Non renseigné' ? null : g.label)}`} aria-hidden="true" /> : null}
                      <h2 className="t-h3">{g.label}</h2>
                      <span className="st-filter-n">{g.people.length}</span>
                      {canWrite && ids.length > 0 ? (
                        <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => toggleGroup(g.people)}>
                          {allIn ? 'Désélectionner le groupe' : 'Sélectionner le groupe'}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                  <ul className="ppl-list">
                    {g.people.map((p) => (
                      <li key={p.id}>
                        <PersonRow
                          person={p}
                          onOpen={() => open(p)}
                          {...(canWrite ? { selected: chosen.has(p.id), onSelect: pickable(p) ? () => toggle(p.id) : undefined } : {})}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })
          )}
          {canWrite && selected.length > 0 ? (
            <SelectionBar projectId={projectId} people={all.filter((p) => chosen.has(p.id))} onClear={() => setSelected([])} />
          ) : null}
        </>
      )}
    </main>
  );
}

/**
 * La barre de la sélection : combien, dont combien déjà invitées ou très
 * sollicitées, puis « Inviter » dans une étude existante ou une nouvelle.
 */
function SelectionBar({ projectId, people, onClear }: { projectId: string; people: Person[]; onClear: () => void }) {
  const { navigate } = useRouter();
  const studies = useStudies(projectId);
  const [studyId, setStudyId] = useState('');
  const withSessions = (studies.data ?? []).filter((s) => {
    const k = studyKind(s.type);
    return k !== 'audit' && k !== 'questionnaire' && !s.is_baseline;
  });
  const invited = people.filter((p) => p.active_invite).length;
  const busy = people.filter((p) => p.recent_invitations >= OVER_SOLICITED).length;
  const go = (path: string) => {
    presetPeople(people.map((p) => p.id));
    navigate(`/projet/${encodeURIComponent(projectId)}${path}`);
  };
  return (
    <div className="ppl-selbar" role="region" aria-label="Personnes sélectionnées">
      <span>
        <strong>{people.length}</strong> sélectionnée{people.length > 1 ? 's' : ''}
        {invited ? <span className="inv-sub"> dont {invited} déjà invitée{invited > 1 ? 's' : ''}</span> : null}
        {busy ? <span className="inv-sub"> · {busy} très sollicitée{busy > 1 ? 's' : ''}</span> : null}
      </span>
      <Select aria-label="Étude" value={studyId} onChange={(e) => setStudyId(e.target.value)}>
        <option value="">Inviter dans l’étude…</option>
        {withSessions.map((s) => <option key={s.id} value={s.id}>{[s.code, s.objective].filter(Boolean).join(' — ')}</option>)}
      </Select>
      <Button variant="accent" disabled={!studyId} onClick={() => go(`/etudes/${encodeURIComponent(studyId)}/inviter`)}>
        Inviter
      </Button>
      <Button variant="ghost" onClick={() => go('/etudes/nouvelle/entretien')}>Nouvelle étude avec elles</Button>
      <Button variant="link" size="s" className="u-push-end" type="button" onClick={onClear}>Tout désélectionner</Button>
    </div>
  );
}

/** Une ligne de l'annuaire : qui, quoi, ses signes et où elle en est. Un clic ouvre sa fiche. */
export function PersonRow({
  person: p,
  onOpen,
  selected,
  onSelect,
}: {
  person: Person;
  onOpen?: () => void;
  selected?: boolean;
  onSelect?: () => void;
}) {
  const body = (
    <>
      <PersonAvatar person={p} />
      <span className="ppl-main">
        <span className="ppl-name">
          {personName(p)}
          <FunBadges person={p} />
          <SeniorityStars value={p.seniority} />
          {p.do_not_contact ? <Chip state="erreur">Ne plus solliciter</Chip> : null}
        </span>
        <span className="ppl-sub">
          {[p.job_title, p.role, p.email].filter(Boolean).join(' · ') || 'Fonction non renseignée'}
        </span>
      </span>
      <PersonChips person={p} />
    </>
  );
  return (
    <div className={`ppl-row-wrap${selected ? ' ppl-row-selected' : ''}`}>
      {selected !== undefined ? (
        <input
          type="checkbox"
          className="zy-check ppl-row-check"
          aria-label={`Sélectionner ${personName(p)}`}
          checked={Boolean(selected)}
          disabled={!onSelect}
          onChange={() => onSelect?.()}
        />
      ) : null}
      {onOpen ? <button type="button" className="ppl-row" onClick={onOpen}>{body}</button> : <div className="ppl-row">{body}</div>}
    </div>
  );
}

export function PersonChips({ person: p }: { person: Person }) {
  return (
    <span className="chips ppl-chips">
      <SolicitationChip person={p} overSolicited={OVER_SOLICITED} />
      {p.pool ? <Chip hue={hueOf(p.pool)}>{p.pool}</Chip> : null}
      {p.engagement ? <Chip>Engagement {engagementLabel(p.engagement)?.toLowerCase()}</Chip> : null}
      {p.persona_name ? <Chip hue="pink">Persona : {p.persona_name}</Chip> : null}
      {p.studies > 0 ? <Chip state="vide">{p.studies} étude{p.studies > 1 ? 's' : ''}</Chip> : null}
    </span>
  );
}
